import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOrganization, useUser } from '@clerk/clerk-react'
import { toast } from 'sonner'
import { useConfiguration } from '../hooks/useConfiguration'
import type { ReactNode } from 'react'

interface ProtectedAgentRouteProps {
  agentId: 'david' | 'hanna'
  children: ReactNode
}

/**
 * Protected route wrapper for agent pages.
 *
 * Checks if:
 * 1. User is authenticated
 * 2. Organization configuration is available
 * 3. Specific agent is enabled for the organization
 *
 * If any check fails, redirects to home page (which handles sign-in).
 * Shows loading spinner while checking access.
 *
 * This is the modern approach to route protection, preventing components from mounting
 * before access is verified. This will eventually be replaced with route loaders when
 * migrating to a routing library that supports them (e.g., React Router 6.4+ loaders).
 */
export function ProtectedAgentRoute({
  agentId,
  children,
}: ProtectedAgentRouteProps) {
  const navigate = useNavigate()
  const { isSignedIn, isLoaded: userLoaded } = useUser()
  const { isLoaded: orgLoaded } = useOrganization()
  const { configuration, loading, error } = useConfiguration()

  useEffect(() => {
    // Skip if Clerk is still loading
    if (!userLoaded || !orgLoaded) return

    // Case 0: User is not signed in - redirect to home for sign-in
    if (!isSignedIn) {
      navigate('/', { replace: true })
      return
    }

    // Skip if configuration is still loading
    if (loading) return

    // Case 1: Configuration failed to load or org not configured
    if (error || !configuration) {
      toast.error(
        'This organization is not configured. Please contact support to enable agents.',
        {
          duration: 5000,
        }
      )
      navigate('/', { replace: true })
      return
    }

    // Case 2: Agent is disabled for this organization
    const agentEnabled = configuration.agents?.[agentId]?.enabled ?? false
    if (!agentEnabled) {
      toast.error('This agent is disabled.', { duration: 5000 })
      navigate('/', { replace: true })
      return
    }
  }, [
    configuration,
    loading,
    error,
    agentId,
    navigate,
    orgLoaded,
    userLoaded,
    isSignedIn,
  ])

  // Show loading spinner while Clerk is loading
  if (!userLoaded || !orgLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  // Redirect will happen via useEffect, show spinner in the meantime
  if (!isSignedIn) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  // Show loading spinner while configuration is loading
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  // If we reach here, access is granted
  return <>{children}</>
}
